import {
  FilterModel,
  getApplicableBedRules,
  getHotelHType,
  getHotelSupplierKey,
  getHTypeFilter,
  getSearchRoomsString,
  getSupplierItemKey,
  mergeHTypeFilters,
  roomMatchesSupplier,
  RoomType,
  RoomTypeGrp,
  RoomTypeOption,
  SupplierItem,
} from 'ts/hotel/classes/FilterModel';
import {
  AvailableBoardType,
  GroupedRooms,
  Hotel,
  HotelRoom,
  RoomCheapest,
  RoomClass,
  RoomRecommend,
} from 'ts/hotel/interfaces/HotelResponse';
import { getDistanceByCoords, getCurrency, addDays } from 'utils/utils';
import { GeoPolygon, HotelRoomId, Price } from 'ts/hotel/interfaces/HotelDetails';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { i18nSharedConfig } from 'utils/shared/utils';
import { User } from 'ts/shared/interfaces/User';
import { i18nConfig as i18nUiConfig } from 'utils/ui';
import { format } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import { Position, RoomByEat } from 'ts/hotel/interfaces/Generic';
import { SerpResult } from 'ts/hotel/interfaces/SerpResponse';
import { getCurrencyBanner, getFacilityTree } from 'services/hotel';
import { flyeastCompanyId } from 'constants/hotel';

export const dateFormat = (date: Date | string | undefined, formatStr: string, locale?: string) => {
  const lang = locale || localStorage.getItem('language');

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (dateObj instanceof Date && isNaN(dateObj.getTime())) return '';
  return format(dateObj || new Date(), formatStr, { locale: lang === 'en' ? enUS : he });
};

export interface i18nHotelConfigProps {
  enTranslation: any;
  heTranslation: any;
  itTranslation?: any;
  esTranslation?: any;
  frTranslation?: any;
}

export const i18nHotelConfig = (props: i18nHotelConfigProps) => {
  const lang = localStorage.getItem('language');

  i18nSharedConfig({ ...props, ruTranslation: {} });
  i18nUiConfig(props);

  i18n.use(initReactI18next).init({
    resources: {
      en: {
        translation: props.enTranslation,
      },
      he: {
        translation: props.heTranslation,
      },
      it: {
        translation: props.itTranslation || {},
      },
      es: {
        translation: props.esTranslation || {},
      },
      fr: {
        translation: props.frTranslation || {},
      },
    },
    lng: lang ? lang : 'he',
    fallbackLng: 'he',

    interpolation: {
      escapeValue: false,
    },
  });
};

export default i18n;

export const applyHTypeCounts = (filter: FilterModel, hotels: Hotel[]): FilterModel => ({
  ...filter,
  hType: getHTypeFilter(hotels, filter.hType?.value),
});

export const getSerpColorCounts = (hotels: Hotel[]) => {
  let red = 0;
  let green = 0;
  let orange = 0;

  for (const hotel of hotels) {
    const color = hotel.serpResult?.color;
    if (color === 'red') red += 1;
    else if (color === 'green') green += 1;
    else if (color === 'orange') orange += 1;
  }

  return { red, green, orange };
};

export const upsertHotelsByBpid = (prev: Hotel[], updates: Hotel[]): Hotel[] => {
  if (!updates.length) return prev;

  const updatesMap = new Map<string, Hotel>();
  for (const hotel of updates) {
    const bpid = hotel.item?.bpid;
    if (bpid) updatesMap.set(bpid, hotel);
  }

  const next: Hotel[] = [];
  const used = new Set<string>();

  for (const hotel of prev) {
    const bpid = hotel.item?.bpid;
    const updated = bpid ? updatesMap.get(bpid) : undefined;
    if (updated) {
      next.push(updated);
      used.add(bpid!);
    } else {
      next.push(hotel);
    }
  }

  for (const hotel of updates) {
    const bpid = hotel.item?.bpid;
    if (!bpid || used.has(bpid)) continue;
    next.push(hotel);
    used.add(bpid);
  }

  return next;
};

export const pickHotelsByBpids = (hotels: Hotel[], bpids: Set<string>): Hotel[] => {
  if (!bpids.size) return [];
  return hotels.filter((hotel) => !!hotel.item?.bpid && bpids.has(hotel.item.bpid));
};

export const mergeFilters = (
  filter?: FilterModel,
  newFilter?: FilterModel,
): FilterModel | undefined => {
  if (!filter || !newFilter) return filter || newFilter;

  const mergedMinPrice =
    filter.price.min !== 0 && newFilter.price.min !== 0
      ? Math.min(filter.price.min, newFilter.price.min)
      : filter.price.min !== 0
        ? filter.price.min
        : newFilter.price.min;
  const mergedMaxPrice = Math.max(filter.price.max, newFilter.price.max);

  const mergedPriceValue: [number, number] = [
    Math.max(mergedMinPrice, Math.min(filter.price.value[0], mergedMaxPrice)),
    Math.min(mergedMaxPrice, Math.max(filter.price.value[1], mergedMinPrice)),
  ];

  const mergedPrice = {
    min: mergedMinPrice,
    max: mergedMaxPrice,
    currencySign: filter.price.currencySign || newFilter.price.currencySign,
    value: mergedPriceValue,
  };

  const mergedStarsOptions = Array.from(
    new Set([...filter.stars.options, ...newFilter.stars.options]),
  ).sort((a, b) => a - b);
  const mergedStarsValue = filter.stars.value.filter((star) => mergedStarsOptions.includes(star));

  const mergedMealActive = Array.from(new Set([...filter.meal.active, ...newFilter.meal.active]));
  const mergedMealValue = filter.meal.value.filter((meal) => mergedMealActive.includes(meal));

  const suppliersMap = new Map<string, SupplierItem>();

  filter.suppliers.forEach((supplier) => {
    suppliersMap.set(getSupplierItemKey(supplier), {
      ...supplier,
      hotelsArr: supplier.hotelsArr || [],
    });
  });

  newFilter.suppliers.forEach((supplier) => {
    const supplierKey = getSupplierItemKey(supplier);
    const existing = suppliersMap.get(supplierKey);
    if (existing) {
      const mergedHotelsArr = Array.from(
        new Set([...(existing.hotelsArr || []), ...(supplier.hotelsArr || [])]),
      );
      suppliersMap.set(supplierKey, {
        suppDesc: existing.suppDesc || supplier.suppDesc,
        suppCode: existing.suppCode || supplier.suppCode,
        checked: existing.checked,
        hotels: existing.hotels + supplier.hotels,
        rooms: existing.rooms + supplier.rooms,
        price:
          supplier.rooms === 0
            ? existing.price
            : existing.rooms === 0
              ? supplier.price
              : Math.min(existing.price, supplier.price),
        hotelsArr: mergedHotelsArr,
      });
    } else {
      suppliersMap.set(supplierKey, {
        ...supplier,
        hotelsArr: supplier.hotelsArr || [],
      });
    }
  });

  const mergedSuppliers = Array.from(suppliersMap.values()).sort((a, b) =>
    a.suppDesc?.localeCompare(b.suppDesc),
  );

  const roomTypesMap = new Map<string, RoomTypeGrp>();

  filter.roomTypes.forEach((type) => {
    roomTypesMap.set(type.title, { ...type });
  });

  newFilter.roomTypes.forEach((newType) => {
    const existingType = roomTypesMap.get(newType.title);

    if (existingType) {
      const optionsMap = new Map<string, RoomTypeOption>();

      existingType.options.forEach((option) => {
        optionsMap.set(option.name, { ...option });
      });

      newType.options.forEach((option) => {
        const existing = optionsMap.get(option.name);
        if (existing) {
          optionsMap.set(option.name, {
            name: option.name,
            checked: existing.checked,
            count: existing.count + option.count,
            price: Math.min(existing.price, option.price),
            visible: existing.visible !== false && option.visible !== false,
          });
        } else {
          optionsMap.set(option.name, { ...option });
        }
      });

      roomTypesMap.set(newType.title, {
        title: newType.title,
        options: Array.from(optionsMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
        icon: existingType.icon || newType.icon,
      });
    } else {
      roomTypesMap.set(newType.title, { ...newType });
    }
  });

  const mergedRoomTypes: RoomTypeGrp[] = Array.from(roomTypesMap.values());

  const additionalOptionsMap = new Map<string, AvailableBoardType>();

  filter.additional.options.forEach((opt) => {
    additionalOptionsMap.set(opt.code, opt);
  });

  newFilter.additional.options.forEach((opt) => {
    additionalOptionsMap.set(opt.code, opt);
  });

  const mergedAdditionalOptions = Array.from(additionalOptionsMap.values());
  const mergedAdditionalValue = filter.additional.value.filter((val) =>
    mergedAdditionalOptions.some((opt) => opt.code === val),
  );

  const mergedLocation =
    filter.location.value.name || filter.location.km ? filter.location : newFilter.location;

  const mergedName = filter.name || newFilter.name;
  const mergedRoom = filter.room || newFilter.room;

  return {
    name: mergedName,
    room: mergedRoom,
    price: mergedPrice,
    stars: {
      options: mergedStarsOptions,
      value: mergedStarsValue,
    },
    meal: {
      options: filter.meal.options,
      active: mergedMealActive,
      value: mergedMealValue,
      tooltips: filter.meal.tooltips || newFilter.meal.tooltips,
    },
    location: mergedLocation,
    additional: {
      options: mergedAdditionalOptions,
      value: mergedAdditionalValue,
      freeCxl: filter.additional.freeCxl,
      withMeal: filter.additional.withMeal,
      preffered: filter.additional.preffered,
      ada: filter.additional.ada,
    },
    suppliers: mergedSuppliers,
    roomTypes: mergedRoomTypes,
    roomTypesArr: getRoomTypesArr(mergedRoomTypes),
    searchWithActiveFilters: filter.searchWithActiveFilters ?? newFilter.searchWithActiveFilters,
    displayExactly: filter.displayExactly ?? newFilter.displayExactly,
    bedRules: newFilter.bedRules || filter.bedRules,
    hType: mergeHTypeFilters(filter.hType, newFilter.hType),
    bedFilter: {
      include: filter.bedFilter?.include ?? newFilter.bedFilter?.include ?? [],
      exclude: filter.bedFilter?.exclude ?? newFilter.bedFilter?.exclude ?? [],
    },
    facilityFilter: {
      include: filter.facilityFilter?.include ?? newFilter.facilityFilter?.include ?? [],
    },
    pinOnly: filter.pinOnly ?? newFilter.pinOnly ?? false,
  };
};

export const formatPriceHotel = (
  price: Price,
  currency: string,
  priceMode?: string,
  afterComma2?: boolean,
) => {
  const currencySymbolBefore = sessionStorage.getItem('currencySymbolBefore') === 'true';
  const currencySign = getCurrency(currency || price.currency);
  let value = price[currency?.toLowerCase() as keyof Price] || price.value;

  if (priceMode === '1' && price.llv && price.llv !== '0') {
    value = value * parseFloat(price.llv);
  } else if (priceMode === '2' && price.mv && price.mv !== '0') {
    value = value * parseFloat(price.mv);
  }

  let currencyClass = '';
  let currencyClass2 = '';
  if (priceMode === '1') {
    currencyClass = '<span class="text-darkviolet">';
    currencyClass2 = '</span>';
  } else if (priceMode === '2') {
    currencyClass = '<span class="text-success">';
    currencyClass2 = '</span>';
  }
  const valuResult = afterComma2 ? value.toFixed(2) : Math.round(value);
  const curr = `${currencyClass}${currencySign}${currencyClass2}`;
  const val = `${valuResult.toLocaleString()}`;
  const result = currencySymbolBefore ? `${curr}${val}` : `${val}${curr}`;
  return result;
};

export const formatPriceDynamic = (price: number, currencySign: string) => {
  const currencySymbolBefore = sessionStorage.getItem('currencySymbolBefore') === 'true';
  let val = '';
  let curr = currencySign;
  if (price === 0) {
    val = '0';
  } else if (price < 0) {
    val = `${Math.abs(Math.round(price)).toLocaleString()}`;
    curr = `-${currencySign}`;
  } else if (price > 0) {
    val = `${Math.round(price).toLocaleString()}`;
    curr = `+${currencySign}`;
  }
  const result = currencySymbolBefore ? `${curr}${val}` : `${val}${curr}`;
  return result;
};

export const getFilterWithPreviousValues = (
  newFilter: FilterModel,
  previousFilter: FilterModel,
) => {
  const newRoomTypes = newFilter.roomTypes?.map((type) => {
    const previousType = previousFilter.roomTypes.find((prevType) => prevType.title === type.title);

    const newOptions = type.options.map((option) => {
      const previousOption = previousType?.options.find(
        (prevOption) => prevOption.name === option.name,
      );

      return {
        ...option,
        checked: previousOption ? previousOption.checked : false,
      };
    });

    return {
      ...type,
      options: newOptions,
    };
  });

  const newSupp = newFilter.suppliers?.map((supplier) => {
    const previousSupplier = previousFilter.suppliers.find(
      (prevSupplier) =>
        getSupplierItemKey(prevSupplier) === getSupplierItemKey(supplier) ||
        (!!supplier.suppCode && prevSupplier.suppCode === supplier.suppCode),
    );
    return {
      ...supplier,
      checked: previousSupplier ? previousSupplier.checked : false,
    };
  });

  const newFilterPreviousValues: FilterModel = {
    name: previousFilter.name!,
    room: previousFilter.room!,
    price: {
      ...newFilter.price,
      value: [
        previousFilter.price.min === previousFilter.price.value[0]
          ? newFilter.price.min
          : previousFilter.price.value[0]!,
        previousFilter.price.max === previousFilter.price.value[1]
          ? newFilter.price.max
          : previousFilter.price.value[1]!,
      ],
    },
    stars: {
      ...newFilter.stars,
      value: previousFilter.stars.value!,
    },
    location: {
      ...newFilter.location,
      km: previousFilter.location.km!,
      value: previousFilter.location.value!,
    },
    meal: {
      ...newFilter.meal,
      active: newFilter.meal.active,
      value: previousFilter.meal.value || [],
    },
    additional: {
      ...newFilter.additional,
      value: previousFilter.additional.value!,
      freeCxl: previousFilter.additional.freeCxl!,
      withMeal: previousFilter.additional.withMeal!,
      preffered: previousFilter.additional.preffered!,
      ada: previousFilter.additional.ada!,
    },
    roomTypes: newRoomTypes,
    roomTypesArr: getRoomTypesArr(newRoomTypes),
    suppliers: newSupp,
    searchWithActiveFilters: previousFilter.searchWithActiveFilters,
    displayExactly: previousFilter.displayExactly,
    bedRules: newFilter.bedRules,
    bedFilter: {
      include: (previousFilter.bedFilter?.include ?? []).filter((term) =>
        (newFilter.bedRules || []).some((group) =>
          group.items?.some((item) => item.positive?.includes(term)),
        ),
      ),
      exclude: (previousFilter.bedFilter?.exclude ?? []).filter((term) =>
        (newFilter.bedRules || []).some((group) =>
          group.items?.some((item) => item.negative?.includes(term)),
        ),
      ),
    },

    facilityFilter: {
      include: previousFilter.facilityFilter?.include ?? [],
    },
    pinOnly: previousFilter.pinOnly || false,
    hType: newFilter.hType
      ? {
          ...newFilter.hType,
          value: previousFilter.hType?.value ?? [],
        }
      : newFilter.hType,
  };

  return newFilterPreviousValues;
};

const addFacilityCodesFromRaw = (codes: Set<string>, raw: unknown) => {
  if (!raw) {
    return;
  }
  if (Array.isArray(raw)) {
    raw.forEach((code) => {
      const value = String(code).trim();
      if (value) {
        codes.add(value);
      }
    });
    return;
  }
  if (typeof raw === 'string') {
    raw.split(';').forEach((code) => {
      const value = code.trim();
      if (value) {
        codes.add(value);
      }
    });
  }
};

export const getHotelFacilityCodes = (hotel: Hotel): Set<string> => {
  const codes = new Set<string>();
  addFacilityCodesFromRaw(codes, hotel.info?.hotelFacility);
  addFacilityCodesFromRaw(codes, hotel.info?.roomFacility);
  return codes;
};

const getFacilityCodeSubgroupKey = (code: string): string => {
  for (const group of getFacilityTree().Tree || []) {
    for (const subgroup of group.subgroups || []) {
      if ((subgroup.facilities || []).some((facility) => facility.facility_code === code)) {
        return `${group.group_code}-${subgroup.subgroup_code}`;
      }
    }
  }
  return code;
};

const hotelHasFacilityCodes = (hotel: Hotel, codes: string[]): boolean => {
  if (!codes.length) {
    return true;
  }

  const hotelCodes = getHotelFacilityCodes(hotel);
  const bySubgroup = new Map<string, string[]>();
  codes.forEach((code) => {
    const key = getFacilityCodeSubgroupKey(code);
    const list = bySubgroup.get(key);
    if (list) {
      list.push(code);
    } else {
      bySubgroup.set(key, [code]);
    }
  });

  return Array.from(bySubgroup.values()).every((groupCodes) =>
    groupCodes.some((code) => hotelCodes.has(code)),
  );
};

export const getFilteredHotels = (
  newFitler: FilterModel,
  currency: string,
  hotels?: Hotel[],
  geoPolygons?: GeoPolygon[][],
  pinnedHotelsIds?: string[],
): Hotel[] => {
  const skipFilterByName = !newFitler.name.trim();
  const skipStars = newFitler.stars.value.length === 0;
  const skipAdditional = newFitler.additional.value.length === 0;
  const skipFreeCxl = !newFitler.additional.freeCxl;
  const skipFreePref = !newFitler.additional.preffered;
  const skipFreeKm = !newFitler.location.km;
  const skipFilterByRoomName = !newFitler.room.trim();
  const roomTerms = newFitler.room
    .toLowerCase()
    .split('|')
    .map((term) => term.trim())
    .filter(Boolean);
  const skipFilterByMeal = !newFitler.additional.withMeal;
  const skipFilterByMeal2 = !newFitler.meal.value?.length;
  const skipFilterByHType = !newFitler.hType?.value.length;
  const skipPinOnly = !newFitler.pinOnly;
  const pinnedIds = new Set(pinnedHotelsIds || []);
  const skipPrice =
    newFitler.price.value[0] === newFitler.price.min &&
    newFitler.price.value[1] === newFitler.price.max;
  const roomTypesChecked = newFitler.roomTypes.reduce(
    (acc, rtype) => {
      const checkedTypes = rtype.options
        .filter((option) => option.checked)
        .map((option) => option.name);
      acc[rtype.title as RoomType] = new Set(checkedTypes);
      return acc;
    },
    {} as Record<string, Set<string>>,
  );
  const skipRoomTypes = !Object.values(roomTypesChecked).some(
    (value) => value instanceof Set && value.size > 0,
  );
  const skipFilterBySupplier = newFitler.suppliers.every((supplier) => !supplier.checked);
  const applicableBedRules = getApplicableBedRules(newFitler.bedRules, getSearchRoomsString());
  const allowedInclude = new Set(
    applicableBedRules.flatMap((group) =>
      (group.items || []).flatMap((item) => item.positive || []),
    ),
  );
  const allowedExclude = new Set(
    applicableBedRules.flatMap((group) =>
      (group.items || []).flatMap((item) => item.negative || []),
    ),
  );
  const bedInclude = (newFitler.bedFilter?.include || []).filter((term) =>
    allowedInclude.has(term),
  );
  const bedExclude = (newFitler.bedFilter?.exclude || []).filter((term) =>
    allowedExclude.has(term),
  );
  const skipFilterByBed = !bedInclude.length && !bedExclude.length;
  const facilityInclude = newFitler.facilityFilter?.include || [];
  const skipFilterByFacility = !facilityInclude.length;

  const newHotels = hotels?.map((hotel) => {
    const hotelHType = getHotelHType(hotel);
    const filteredByHType =
      skipFilterByHType || (!!hotelHType && !!newFitler.hType?.value.includes(hotelHType));
    const filteredByFacility =
      skipFilterByFacility || hotelHasFacilityCodes(hotel, facilityInclude);

    const filteredByName =
      skipFilterByName ||
      newFitler.name
        .toLowerCase()
        .split('|')
        .map((term) => term.trim())
        .filter(Boolean)
        .some((term) =>
          term.split(' ').every((name) => hotel.item.name.toLowerCase().includes(name)),
        );

    const filteredByStars =
      skipStars ||
      newFitler.stars.value.some((star) => {
        return Math.floor(hotel.item?.star || 0) === star;
      });
    const filteredByPreffered =
      skipFreePref || (newFitler.additional.preffered && !!hotel.priority?.internalBestVal);

    const filteredByPin =
      skipPinOnly || pinnedIds.has(getOriginalHotelKey(hotel.htuKey));

    const filteredByKm = skipFreeKm || (newFitler.location.km || 0) >= +hotel.distance;

    let filteredByDisplayExactly = true;
    if (geoPolygons?.length && newFitler.displayExactly) {
      const point = { lat: hotel.position.latitude, lng: hotel.position.longitude };

      filteredByDisplayExactly = isPointInPolygons(point, geoPolygons);
    } else if (newFitler.displayExactly) {
      filteredByDisplayExactly = !newFitler.displayExactly || hotel.position.pip === '1';
    }

    const roomClasses = hotel.roomClasses.map((room) => {
      const filteredByName =
        skipFilterByRoomName ||
        roomTerms.some((term) =>
          term
            .split(' ')
            .every((name) =>
              room.hotelRooms.some((hotelRoom) =>
                hotelRoom.displayName.toLowerCase().includes(name),
              ),
            ),
        );

      const filteredByMeal =
        skipFilterByMeal || (newFitler.additional.withMeal && room.board.basis.code !== 'RO');
      const filteredByMeal2 =
        skipFilterByMeal2 ||
        newFitler.meal.value.some((meal) => meal === room.board.basis.code) ||
        (newFitler.meal.value.includes('BB') && room.board.basis.code !== 'RO');

      const filteredByAdditional =
        skipAdditional || newFitler.additional.value.includes(room.board.basis.code);

      const filteredByCxl =
        skipFreeCxl ||
        (newFitler.additional.freeCxl && new Date(room.cxl.limitDate) > new Date()) ||
        new Date(room.cxl.limitDate) < new Date(2000, 1, 1);
      const filteredByPrice =
        skipPrice ||
        (room.price[currency as keyof Price] >= newFitler.price.value[0] &&
          room.price[currency as keyof Price] <= newFitler.price.value[1]);

      const filteredByRoomTypes =
        skipRoomTypes ||
        newFitler.roomTypes.every((rtype) => {
          const mode = rtype.title as RoomType;
          const checkedTypes = roomTypesChecked[mode] || new Set<string>();
          const modeKey = mode as keyof HotelRoom;
          const roomTypesInRoom = room.hotelRooms
            .filter((hRoom) => hRoom[modeKey])
            ?.flatMap((hRoom) => hRoom[modeKey].split(', '));

          return !checkedTypes.size || roomTypesInRoom.some((type) => checkedTypes.has(type));
        });

      const filteredBySupplier =
        skipFilterBySupplier ||
        newFitler.suppliers.some(
          (supplier) => supplier.checked && roomMatchesSupplier(room, supplier),
        );
      const roomNames = skipFilterByBed ? [] : getRoomNameValuesForBedFilter(room);
      const filteredByBed =
        skipFilterByBed ||
        ((!bedInclude.length ||
          bedInclude.some((term) => roomNameMatchesBedTerm(roomNames, term, true))) &&
          !bedExclude.some((term) => roomNameMatchesBedTerm(roomNames, term)));

      return {
        ...room,
        visible:
          filteredByName &&
          filteredByMeal &&
          filteredByMeal2 &&
          filteredByAdditional &&
          filteredByCxl &&
          filteredByPrice &&
          filteredByRoomTypes &&
          filteredBySupplier &&
          filteredByBed,
      };
    });

    const visible =
      filteredByName &&
      filteredByHType &&
      filteredByStars &&
      filteredByKm &&
      filteredByPreffered &&
      filteredByPin &&
      filteredByFacility &&
      filteredByDisplayExactly &&
      roomClasses.some((room) => room.visible && room.visibleFiltered);

    return {
      ...hotel,
      visible,
      roomClasses,
    };
  });

  return newHotels || [];
};

export const getCenteredHotel = (hotel?: Hotel) => {
  if (!hotel || !hotel.position) return;
  return {
    lat: hotel.position.latitude,
    lng: hotel.position.longitude,
    htuKey: hotel.htuKey,
    name: hotel.item?.name,
    address: hotel.addressInfo?.address,
  };
};

const getMergeHotelPriority = (): string[] => {
  try {
    const initSiteData = JSON.parse(localStorage.getItem('initSiteData') || '{}');
    const sharedUI =
      typeof initSiteData.sharedUI === 'string'
        ? JSON.parse(initSiteData.sharedUI)
        : initSiteData.sharedUI || {};
    return Array.isArray(sharedUI.mergeHotelPriority) ? sharedUI.mergeHotelPriority : [];
  } catch {
    return [];
  }
};

const getHotelMergePriorityIndex = (hotel: Hotel, priority: string[]) => {
  if (!priority.length) return Infinity;

  const sysSupp = (hotel.item?.sysSupp || '').toLowerCase();
  const sysSuppCode = (hotel.item?.sysSuppCode || '').toLowerCase();
  const index = priority.findIndex((item) => {
    const key = item?.toLowerCase();
    return key && (key === sysSupp || key === sysSuppCode);
  });

  return index === -1 ? Infinity : index;
};

export const getAllHotelsWithoutDuplicates = (
  prevHotels: Hotel[],
  newHotels: Hotel[],
  areRoomsFiltered: boolean,
): Hotel[] => {
  let result = [...newHotels].map((hotel) => ({
    ...hotel,
    roomClasses: hotel.roomClasses.map((room) => ({
      ...room,
      suppDesc: getHotelSupplierKey(hotel),
      suppCode: hotel.item?.sysSuppCode,
      bpid: hotel.item?.bpid,
      htuKey: hotel.htuKey,
      hitemCode: hotel.itemCode,
    })),
  })) as Hotel[];

  result = [...prevHotels, ...result];

  const bpids = result.map((hotel) => hotel.item?.bpid);

  const uniqueKeys = bpids.filter((key, index, arr) => arr.indexOf(key) === index);

  const duplicates = uniqueKeys
    .map((key) => ({
      key: key,
      hotels: result.filter((hotel) => hotel.item?.bpid === key),
    }))
    .filter((hotel) => hotel.hotels.length > 1);

  const mergeHotelPriority = getMergeHotelPriority();

  const mergedDuplicatesHotels = duplicates.map((duplicate) => {
    const hotelsByPriority = [...duplicate.hotels].sort(
      (a, b) =>
        getHotelMergePriorityIndex(a, mergeHotelPriority) -
        getHotelMergePriorityIndex(b, mergeHotelPriority),
    );

    return hotelsByPriority.reduce((acc, curr) => {
      if (!acc) {
        return curr;
      } else {
        const newRoomClasses = curr.roomClasses.map((room) => {
          return room;
        });

        const roomClasses = [...acc.roomClasses, ...newRoomClasses].filter(
          (room, index, arr) => arr.findIndex((r) => r.rcUniqueKey === room.rcUniqueKey) === index,
        );
        const item = {
          ...acc.item,
          type: acc.item?.type || curr.item?.type,
        };

        if (areRoomsFiltered) {
          return { ...acc, item, roomClasses: filterRoomClasses(roomClasses) };
        }
        return { ...acc, item, roomClasses };
      }
    });
  });

  mergedDuplicatesHotels.forEach((hotel) => {
    const index = result.findIndex((item) => item.item?.bpid === hotel.item?.bpid);
    result = result.filter((item) => item.item?.bpid !== hotel.item?.bpid);

    result.splice(index, 0, hotel);
  });

  return result;
};
export const clearRoomNamePatterns = [
  /bed and breakfast/gi,
  /free wifi/gi,
  /nonsmoking/gi,
  /non smoking/gi,
  /non-smoking/gi,
  /package rate/gi,
  /non refundable/gi,
  /non refunduble/gi,
  /non-refundable/gi,
  /breakfast included in the price/gi,
  /room only/gi,
  /breakfast included/gi,
  /\(\)/g,
];
export const formatHotels = async (hotels: Hotel[], hotelIndex?: number): Promise<Hotel[]> => {
  const searchParamsStr = sessionStorage.getItem(getKey('lasestSearchUrl', hotelIndex));
  const queryString = searchParamsStr?.split('?')[1];
  const searchParams = new URLSearchParams(queryString);

  let thbUsdRate = 1;
  if (searchParams.get('id') === 'TH') {
    const initSiteData = localStorage.getItem('initSiteData');
    const initJson = JSON.parse(initSiteData || '{}');
    const companyId = initJson.companyId;
    if (companyId === flyeastCompanyId) {
      const currencyBanner = await getCurrencyBanner();
      if (currencyBanner) {
        thbUsdRate =
          currencyBanner.find((currency) => currency.sysCurrencyCode === 'THB')?.convert_usd_rate ||
          1;
        sessionStorage.setItem('thbUsdRate', thbUsdRate.toString());
      }
    }
  }

  return (
    hotels.map((hotel) => {
      return {
        ...hotel,
        visible: true,
        distance: getDistanceByCoords(
          parseFloat(searchParams.get('lat')!),
          parseFloat(searchParams.get('lon')!),
          hotel.position?.latitude || 0,
          hotel.position?.longitude || 0,
        ),
        roomClasses: hotel.roomClasses.map((room) => {
          const result = room.hotelRooms.map((hotelRoom) => {
            let clearedName = `${room.desc ? room.desc + ' - ' : ''}${
              hotelRoom.desc ? hotelRoom.desc : ''
            }`;

            clearRoomNamePatterns.forEach((pattern) => {
              clearedName = clearedName.replaceAll(pattern, '');
            });
            clearedName = clearedName.replaceAll(/[-,]\s*/g, ' ').trim();

            return {
              ...hotelRoom,
              displayNameCleared: clearedName,
              displayName: `${room.desc ? room.desc + ' - ' : ''}${
                hotelRoom.desc ? hotelRoom.desc : ''
              } ${room.board.basis.desc ? room.board.basis.desc : ''}`,
            };
          });

          return {
            ...room,
            hotelRooms: result,
            visibleSm: true,
            visible: true,
            visibleFiltered: true,
            price: {
              ...room.price,
              thb: room.price.usd / thbUsdRate,
            },
          };
        }),
      };
    }) || []
  );
};

export const getRoomTypesArr = (roomType: RoomTypeGrp[]) => {
  let result = roomType
    .filter((it) => it.options.some((opt) => opt.checked))
    .map((it) => {
      return it.options.filter((opt) => opt.checked).map((opt) => opt.name);
    })
    .reduce((acc, val) => acc.concat(val), [])
    .filter((it) => it !== 'accessible');
  return result;
};

export const getLowestRoomPrices = (roomClasses: RoomClass[]) => {
  let result = [];
  const lowestPriceRoomsRed = roomClasses
    .filter(
      (room) =>
        new Date(room.cxl.limitDate) < new Date() &&
        new Date(room.cxl.limitDate) > new Date(2000, 1, 1),
    )
    .sort((a, b) => a.price.usd - b.price.usd)?.[0];
  const lowestPriceRoomsGreen = roomClasses
    .filter(
      (room) =>
        new Date(room.cxl.limitDate) >= new Date() ||
        new Date(room.cxl.limitDate) < new Date(2000, 1, 1),
    )
    .sort((a, b) => a.price.usd - b.price.usd)?.[0];

  if (lowestPriceRoomsRed) {
    result.push({ type: 'red', room: lowestPriceRoomsRed });
  }

  if (lowestPriceRoomsGreen) {
    result.push({ type: 'green', room: lowestPriceRoomsGreen });
  }

  return result.sort((a, b) => a.room.price.value - b.room.price.value);
};

export const getRecomendRooms = (
  roomClasses: RoomClass[],
  lowestPriceRooms: RoomCheapest[],
  recRoomsCount: number,
) => {
  let recRooms = roomClasses.reduce((acc: RoomRecommend[], curr) => {
    const cat = curr.hotelRooms[0].cat;
    const vew = curr.hotelRooms[0].typ;
    if (typeof cat === 'string' && !acc.some((item) => item.data.includes(cat))) {
      acc = [...acc, { type: 'cat', data: cat, rooms: [], visible: true }];
    }
    if (typeof vew === 'string' && !acc.some((item) => item.data.includes(vew))) {
      acc = [...acc, { type: 'typ', data: vew, rooms: [], visible: true }];
    }

    return acc;
  }, []);

  recRooms.forEach((cat) => {
    const roomGreen = roomClasses
      .filter(
        (room) =>
          (new Date(room.cxl.limitDate) > new Date() ||
            new Date(room.cxl.limitDate) < new Date(2000, 1, 1)) &&
          room.hotelRooms[0][cat.type] === cat.data,
      )
      .sort((a, b) => a.price.usd - b.price.usd)?.[0];
    const roomRed = roomClasses
      .filter(
        (room) =>
          new Date(room.cxl.limitDate) < new Date() &&
          new Date(room.cxl.limitDate) > new Date(2000, 1, 1) &&
          room.hotelRooms[0][cat.type] === cat.data,
      )
      .sort((a, b) => a.price.usd - b.price.usd)?.[0];

    let rooms = [];
    if (roomGreen) {
      rooms.push({ type: 'green', room: roomGreen });
    }
    if (roomRed) {
      rooms.push({ type: 'red', room: roomRed });
    }
    cat.rooms = rooms.sort((a, b) => a.room.price.usd - b.room.price.usd);
  });

  recRooms = recRooms.filter(
    (item) => item.rooms?.[0].room.rcUniqueKey !== lowestPriceRooms[0].room.rcUniqueKey,
  );

  if (window.innerWidth > 768 && recRooms.length > recRoomsCount) {
    recRooms = recRooms.map((item, index) => ({
      ...item,
      visible: index < recRoomsCount,
    }));
  }

  return recRooms;
};

export const getGroupedRoomsByEat = (roomClasses: RoomClass[]) => {
  const eatOptions = ['RO', 'BB', 'HB', 'FB', 'AI'];

  let groupedRooms: RoomByEat[] = [];

  eatOptions.forEach((eat) => {
    const cheapestRoomRed = roomClasses
      .filter(
        (room) =>
          room.board.basis.code === eat &&
          new Date(room.cxl.limitDate) < new Date() &&
          new Date(room.cxl.limitDate) > new Date(2000, 1, 1),
      )
      .sort((a, b) => a.price.usd - b.price.usd)?.[0];

    const cheapestRoomGreen = roomClasses
      .filter(
        (room) =>
          room.board.basis.code === eat &&
          (new Date(room.cxl.limitDate) >= new Date() ||
            new Date(room.cxl.limitDate) < new Date(2000, 1, 1)),
      )
      .sort((a, b) => a.price.usd - b.price.usd)?.[0];

    if (cheapestRoomRed || cheapestRoomGreen) {
      const rooms = [
        { type: 'red', room: cheapestRoomRed },
        { type: 'green', room: cheapestRoomGreen },
      ]
        .filter((room) => room.room)
        .sort((a, b) => a.room.price.usd - b.room.price.usd);
      groupedRooms.push({
        eat,
        rooms,
      });
    }
  });

  return groupedRooms;
};

export const getSortedHotels = (
  hotels: Hotel[] | undefined,
  sort: string,
  sortByNameStr: string,
  locationName?: string,
  areRoomsFiltered = true,
  hotelIndex?: number,
) => {
  let newHotels: Hotel[] = [];
  const isVisibleRoom = (rc: RoomClass) =>
    rc.visible && rc.visibleSm !== false && (rc.visibleFiltered || !areRoomsFiltered);
  const getVisibleRoomPrice = (hotel: Hotel, fallbackPrice: number) =>
    hotel.roomClasses.find(isVisibleRoom)?.price.usd ?? fallbackPrice;

  const hotelsSortedRooms = hotels?.map((hot) => {
    const sortedRooms = [...hot.roomClasses].sort((a, b) => {
      const aVisible = isVisibleRoom(a);
      const bVisible = isVisibleRoom(b);

      if (aVisible !== bVisible) {
        return aVisible ? -1 : 1;
      }

      return a.price.usd - b.price.usd;
    });

    return {
      ...hot,
      roomClasses: sortedRooms,
    };
  });

  if (hotelsSortedRooms?.length) {
    if (sort === 'distanceIncrease') {
      newHotels = [...hotelsSortedRooms].sort(
        (a, b) => parseFloat(a.distance) - parseFloat(b.distance),
      );

      const similarHotelSearchName = getHotelNameFromHotelSearchGA() || locationName || '';
      if (similarHotelSearchName) {
        let similarHotelIndex = -1;

        newHotels.forEach((hotel, index) => {
          if (hotel.priority?.suppBestVal) {
            similarHotelIndex = index;
          }
        });

        if (similarHotelIndex !== -1) {
          const similarHotel = newHotels.splice(similarHotelIndex, 1)[0];
          newHotels.unshift({ ...similarHotel, similarHotel: true });
        }
      }
    } else if (sort === 'distanceDecrease') {
      newHotels = [...hotelsSortedRooms].sort(
        (a, b) => parseFloat(b.distance) - parseFloat(a.distance),
      );
    } else if (sort === 'priceIncrease') {
      newHotels = [...hotelsSortedRooms].sort(
        (a, b) =>
          getVisibleRoomPrice(a, Number.MAX_SAFE_INTEGER) -
          getVisibleRoomPrice(b, Number.MAX_SAFE_INTEGER),
      );
    } else if (sort === 'priceDecrease') {
      newHotels = [...hotelsSortedRooms].sort(
        (a, b) =>
          getVisibleRoomPrice(b, Number.MIN_SAFE_INTEGER) -
          getVisibleRoomPrice(a, Number.MIN_SAFE_INTEGER),
      );
    } else if (sort === 'starIncrease') {
      newHotels = [...hotelsSortedRooms].sort((a, b) => a.item.star - b.item.star);
    } else if (sort === 'starDecrease') {
      newHotels = [...hotelsSortedRooms].sort((a, b) => b.item.star - a.item.star);
    } else if (sort === 'nameIncrease') {
      newHotels = [...hotelsSortedRooms].sort((a, b) => a.item.name.localeCompare(b.item.name));
    } else if (sort === 'nameDecrease') {
      newHotels = [...hotelsSortedRooms].sort((a, b) => b.item.name.localeCompare(a.item.name));
    } else if (sort === 'recommended') {
      newHotels = [...hotelsSortedRooms].sort((a, b) => {
        if (a.priority?.internalBestVal === b.priority?.internalBestVal) {
          return a.roomClasses[0].price.usd - b.roomClasses[0].price.usd;
        } else {
          return (b.priority?.internalBestVal || 0) - (a.priority?.internalBestVal || 0);
        }
      });
    } else {
      newHotels = [...hotelsSortedRooms];
    }
  }

  if (sortByNameStr) {
    newHotels.sort((a, b) => {
      const aWords = a.item.name?.toLowerCase().split(' ') || [];
      const bWords = b.item.name?.toLowerCase().split(' ') || [];

      const aHasMatch = aWords.includes(sortByNameStr);
      const bHasMatch = bWords.includes(sortByNameStr);

      if (aHasMatch) return -1;
      if (bHasMatch) return 1;

      return 0;
    });
  }

  const pinnedSimilarHotelIndex = newHotels.findIndex((hotel) => hotel.similarHotel);
  if (pinnedSimilarHotelIndex > 0) {
    const pinnedSimilarHotel = newHotels.splice(pinnedSimilarHotelIndex, 1)[0];
    newHotels.unshift(pinnedSimilarHotel);
  }

  return newHotels;
};

export const isRoomVisible = (room: RoomClass, areRoomsFiltered: boolean) => {
  return room.visible && room.visibleSm && (room.visibleFiltered || !areRoomsFiltered);
};

export const getOriginalHotelKey = (htuKey: string) => {
  const separatorIndex = htuKey.indexOf('__');
  return separatorIndex === -1 ? htuKey : htuKey.slice(0, separatorIndex);
};

export const matchesHotelUkey = (
  hotel: { htuKey?: string; roomClasses?: { htuKey?: string }[] } | null | undefined,
  hotelUkey?: string
) => {
  if (!hotel?.htuKey || !hotelUkey) return false;
  if (hotel.htuKey === hotelUkey) return true;
  if (getOriginalHotelKey(hotel.htuKey) === getOriginalHotelKey(hotelUkey)) return true;
  return !!hotel.roomClasses?.some(
    (rc) =>
      !!rc.htuKey &&
      (rc.htuKey === hotelUkey || getOriginalHotelKey(rc.htuKey) === getOriginalHotelKey(hotelUkey))
  );
};

export const getFlattenedHotelKey = (hotel: Hotel, room: RoomClass) =>
  `${getOriginalHotelKey(hotel.htuKey)}__${room.rcUniqueKey}`;

export const flattenHotelsByRoom = (hotels: Hotel[], areRoomsFiltered: boolean): Hotel[] =>
  hotels.flatMap((hotel) => {
    const visibleRooms =
      hotel.roomClasses?.filter((room) => isRoomVisible(room, areRoomsFiltered)) || [];

    return visibleRooms.map((room) => ({
      ...hotel,
      htuKey: getFlattenedHotelKey(hotel, room),
      roomClasses: [room],
      choosenRoomId: room.rcUniqueKey,
      choosenRoomIds: [room.rcUniqueKey],
    }));
  });

const getMode16RoomPrice = (hotel: Hotel, fallbackPrice: number) =>
  hotel.roomClasses[0]?.price.usd ?? fallbackPrice;

const compareMode16RoomCards = (
  a: Hotel,
  b: Hotel,
  comparePrimary: (a: Hotel, b: Hotel) => number,
) => {
  const primaryDiff = comparePrimary(a, b);

  if (primaryDiff !== 0) {
    return primaryDiff;
  }

  const priceDiff = getMode16RoomPrice(a, 0) - getMode16RoomPrice(b, 0);

  if (priceDiff !== 0) {
    return priceDiff;
  }

  return a.htuKey.localeCompare(b.htuKey);
};

export const getSortedHotelsMode16 = (
  hotels: Hotel[],
  sort: string,
  areRoomsFiltered: boolean,
): Hotel[] => {
  const flattenedHotels = flattenHotelsByRoom(hotels, areRoomsFiltered);
  const sortKey = sort || 'recommended';

  if (sortKey === 'recommended') {
    return [...flattenedHotels].sort((a, b) => {
      if (Boolean(a.similarHotel) !== Boolean(b.similarHotel)) {
        return a.similarHotel ? -1 : 1;
      }

      const priorityDiff = (b.priority?.internalBestVal || 0) - (a.priority?.internalBestVal || 0);

      if (priorityDiff !== 0) {
        return priorityDiff;
      }

      return compareMode16RoomCards(a, b, () => 0);
    });
  }

  if (sortKey === 'distanceIncrease') {
    return [...flattenedHotels].sort((a, b) =>
      compareMode16RoomCards(
        a,
        b,
        (left, right) => parseFloat(left.distance) - parseFloat(right.distance),
      ),
    );
  }

  if (sortKey === 'distanceDecrease') {
    return [...flattenedHotels].sort((a, b) =>
      compareMode16RoomCards(
        a,
        b,
        (left, right) => parseFloat(right.distance) - parseFloat(left.distance),
      ),
    );
  }

  if (sortKey === 'priceIncrease') {
    return [...flattenedHotels].sort(
      (a, b) =>
        getMode16RoomPrice(a, Number.MAX_SAFE_INTEGER) -
        getMode16RoomPrice(b, Number.MAX_SAFE_INTEGER),
    );
  }

  if (sortKey === 'priceDecrease') {
    return [...flattenedHotels].sort(
      (a, b) =>
        getMode16RoomPrice(b, Number.MIN_SAFE_INTEGER) -
        getMode16RoomPrice(a, Number.MIN_SAFE_INTEGER),
    );
  }

  if (sortKey === 'starIncrease') {
    return [...flattenedHotels].sort((a, b) =>
      compareMode16RoomCards(a, b, (left, right) => left.item.star - right.item.star),
    );
  }

  if (sortKey === 'starDecrease') {
    return [...flattenedHotels].sort((a, b) =>
      compareMode16RoomCards(a, b, (left, right) => right.item.star - left.item.star),
    );
  }

  if (sortKey === 'nameIncrease') {
    return [...flattenedHotels].sort((a, b) =>
      compareMode16RoomCards(a, b, (left, right) => left.item.name.localeCompare(right.item.name)),
    );
  }

  if (sortKey === 'nameDecrease') {
    return [...flattenedHotels].sort((a, b) =>
      compareMode16RoomCards(a, b, (left, right) => right.item.name.localeCompare(left.item.name)),
    );
  }

  return [...flattenedHotels].sort(
    (a, b) =>
      getMode16RoomPrice(a, Number.MAX_SAFE_INTEGER) -
      getMode16RoomPrice(b, Number.MAX_SAFE_INTEGER),
  );
};

export const getSortedRooms = (rooms: Hotel[]) => {
  return rooms.map((room) => {
    const sortedRooms = room.roomClasses?.sort((a, b) => a.price.usd - b.price.usd);

    return {
      ...room,
      roomClasses: sortedRooms,
    };
  });
};

export const getSortByFullName = (hotels: Hotel[], filter: FilterModel) => {
  if (filter.name) {
    const matchedWord = hotels.reduce<string>((acc, htl) => {
      if (acc) return acc;

      const nameWords = new Set(htl.item?.name?.toLowerCase().split(' ') || []);
      const filterWords = filter.name?.toLowerCase().split(/[\s|]/).filter(Boolean) || [];

      return filterWords.find((fWord) => nameWords.has(fWord)) || '';
    }, '');

    return matchedWord;
  }

  return '';
};

export const groupRoomClasses = (roomClasses: RoomClass[]): GroupedRooms[] => {
  const groupedRooms = roomClasses.reduce((groupedRooms: GroupedRooms[], roomClass) => {
    roomClass.hotelRooms.forEach((room) => {
      const cat = room.cat || 'un';
      const typ = room.typ || 'un';
      const brd = room.brd || 'un';

      const groupName = `CAt:${cat}/TYP:${typ}/BRD:${brd}`;

      let existingGroup = groupedRooms.find((group) => group.name === groupName);
      if (!existingGroup) {
        existingGroup = { data: [], name: groupName, open: false, index: groupedRooms.length };
        groupedRooms.push(existingGroup);
      }

      existingGroup.data.push(roomClass);
    });

    return groupedRooms;
  }, []);

  return groupedRooms;
};

export const filterRoomClasses = (rooms: RoomClass[]): RoomClass[] => {
  const now = new Date();

  let newRooms = rooms
    .map((room) => {
      const cat = room.hotelRooms[0].cat || 'un';
      const typ = room.hotelRooms[0].typ || 'un';
      const brd = room.hotelRooms[0].brd || 'un';
      const bed = room.hotelRooms[0].bed || 'un';
      const cxlLimitDate = new Date(room.cxl.limitDate);
      const isCxlDateBeforeNow = cxlLimitDate < now && cxlLimitDate > new Date(2000, 1, 1);

      const groupName = `CAT:${cat}/TYP:${typ}/BRD:${brd}/cxl:${isCxlDateBeforeNow}/bed:${bed}`;
      return {
        ...room,
        groupName,
      };
    })
    .sort((a, b) => {
      if (a.groupName === b.groupName) {
        return a.price.usd - b.price.usd;
      }
      return a.groupName.localeCompare(b.groupName);
    });

  const alreadyMarkedGroups: string[] = [];
  newRooms.forEach((room) => {
    if (alreadyMarkedGroups.includes(room.groupName)) {
      room.visibleFiltered = false;
    } else {
      alreadyMarkedGroups.push(room.groupName);
      room.visibleFiltered = true;
    }
  });

  return newRooms;
};

export const getRoomsBTokensForDetails = (filter: FilterModel, hotel: Hotel, htuKey?: string) => {
  const suppFilter = filter?.suppliers.filter((supp) => supp.checked);

  const filteredRoomClasses = hotel.roomClasses.filter((rc) => {
    const filteredBySupp = suppFilter?.length
      ? suppFilter.some((supp) => roomMatchesSupplier(rc, supp))
      : true;

    const filteredByHtuKey = !htuKey || rc.htuKey === htuKey;
    return filteredBySupp && filteredByHtuKey;
  });

  const res = filteredRoomClasses.reduce((acc: HotelRoomId[], rc) => {
    const index = acc.findIndex((item) => item.htuKey === rc.htuKey);
    const bTokens = rc.hotelRooms.map((hr) => hr.bToken);
    if (index === -1) {
      acc.push({
        htuKey: rc.htuKey,
        multiRoomsBTokens: [...bTokens],
        hotelItemCode: rc.hitemCode!,
        supplier: rc.suppCode + ' ' + rc.suppDesc ? rc.suppDesc : '',
      });
    } else {
      acc[index].multiRoomsBTokens.push(...bTokens);
    }
    return acc;
  }, []);

  return res;
};

export const getPriorityDesc = (num: number): string => {
  let result = '';

  switch (num) {
    case 10:
      result = 'hotel.system_recommended';
      break;
    case 20:
      result = 'hotel.travel_agent_recommended';
      break;
    case 30:
    case 50:
      result = 'hotel.client_recommended';
      break;
    default:
      return '';
  }

  return result;
};

export const getMaxOccupancy = (room: HotelRoom) => {
  const adults = room?.numAdt;
  const children = room?.numCnn;

  return `${adults} Adults${children ? `, ${children} Children` : ''}`;
};

export const getCxlClass = (limitDate: string, isShort?: boolean) => {
  const result = 'block-wr block-mh ' + (isShort ? 'block-short-wr ' : '');

  if (new Date(limitDate) < new Date() && new Date(limitDate) > new Date(2000, 1, 1)) {
    return result + 'block-danger';
  } else if (new Date(limitDate) < addDays(2) && new Date(limitDate) > new Date(2000, 1, 1)) {
    return result + 'block-warning';
  } else {
    return result + 'block-success';
  }
};

export const getKey = (key: string, index?: number) => {
  return `${key}${index ? index : ''}`;
};

export const getHotelIndex = (index?: number) => {
  return index && index > 0 ? index + '' : '';
};

export const handleSerpHotel = (hotel: Hotel, serpResults: SerpResult[]) => {
  const serpResult = serpResults.find((serp) => serp.bpid === hotel.item?.bpid);

  if (serpResult) {
    const nonRefRoom = hotel.roomClasses.find((room) => new Date(room.cxl.limitDate) > new Date());

    const compareRoom = nonRefRoom || hotel.roomClasses[0];

    const ourPrice = compareRoom.price[serpResult.currency?.toLowerCase() as keyof Price];
    const serpPrice = serpResult.lowest;

    const priceRatio = ourPrice / serpPrice;

    const diff = 0.03;
    if (priceRatio >= 1 - diff && priceRatio <= 1 + diff) {
      return { ...hotel, serpResult: { ...serpResult, color: 'orange' } };
    } else if (ourPrice < serpPrice) {
      return { ...hotel, serpResult: { ...serpResult, color: 'green' } };
    } else {
      return { ...hotel, serpResult: { ...serpResult, color: 'red' } };
    }
  }

  return hotel;
};

export const pascalToCamel = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (obj instanceof Date) {
    return obj.toISOString();
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => pascalToCamel(item));
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const converted: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        let camelKey = key;

        if (key === key.toUpperCase() && key.length > 1) {
          camelKey = key.toLowerCase();
        } else {
          let acronymEnd = 0;
          for (let i = 0; i < key.length; i++) {
            if (key[i] === key[i].toUpperCase()) {
              if (i + 1 < key.length && key[i + 1] === key[i + 1].toLowerCase()) {
                acronymEnd = i;
                break;
              }
              acronymEnd = i + 1;
            } else {
              break;
            }
          }
          if (acronymEnd > 0) {
            camelKey = key.substring(0, acronymEnd).toLowerCase() + key.substring(acronymEnd);
          } else {
            camelKey = key.charAt(0).toLowerCase() + key.slice(1);
          }
        }

        converted[camelKey] = pascalToCamel(obj[key]);
      }
    }
    return converted;
  }

  return obj;
};

const getRoomNameValuesForBedFilter = (room: RoomClass) => {
  const values = [
    String(room.displayName || ''),
    String(room.desc || ''),
    ...room.hotelRooms.map((hotelRoom) => String(hotelRoom.displayName || hotelRoom.desc || '')),
  ]
    .map((value) => value.replace(/<[^>]*>/g, '').trim())
    .filter(Boolean);

  return Array.from(new Set(values));
};

const roomNameMatchesBedTerm = (names: string[], term: string, rejectNegated = false) => {
  const normalizedTerm = term.toLowerCase().trim();
  const words = normalizedTerm.split(/\s+/).filter(Boolean);

  if (!words.length) {
    return false;
  }

  return names.some((name) => {
    const value = name.toLowerCase();
    if (!words.every((word) => value.includes(word))) {
      return false;
    }
    if (rejectNegated && value.includes(`no ${normalizedTerm}`)) {
      return false;
    }
    return true;
  });
};

export const isPointInPolygons = (
  point: Position,
  polygons: GeoPolygon[][],
  includeBoundary: boolean = true,
): boolean => {
  const validatedPoint = {
    lat: typeof point.lat === 'string' ? parseFloat(point.lat) : point.lat,
    lng: typeof point.lng === 'string' ? parseFloat(point.lng) : point.lng,
  };

  const validatedPolygons = polygons.map((poly) =>
    poly.map((coord) => ({
      lat: typeof coord.lat === 'string' ? parseFloat(coord.lat) : coord.lat,
      lng: typeof coord.lng === 'string' ? parseFloat(coord.lng) : coord.lng,
    })),
  );

  const mightBeSwapped = Math.abs(validatedPoint.lat) > 90 || Math.abs(validatedPoint.lng) > 180;
  if (mightBeSwapped) {
    const swappedPoint = { lat: validatedPoint.lng, lng: validatedPoint.lat };
    const resultWithSwapped = checkPointInPolygons(
      swappedPoint,
      validatedPolygons,
      includeBoundary,
    );
    if (resultWithSwapped) {
      return true;
    }
  }

  return checkPointInPolygons(validatedPoint, validatedPolygons, includeBoundary);
};

function checkPointInPolygons(
  point: Position,
  polygons: GeoPolygon[][],
  includeBoundary: boolean,
): boolean {
  function onSegmentLL(p: Position, q: Position, r: Position): boolean {
    const px: number = p.lng;
    const py: number = p.lat;
    const qx: number = q.lng;
    const qy: number = q.lat;
    const rx: number = r.lng;
    const ry: number = r.lat;

    const cross: number = (qx - px) * (ry - py) - (qy - py) * (rx - px);
    if (Math.abs(cross) > 1e-10) return false;

    const minX: number = Math.min(px, rx) - 1e-10;
    const maxX: number = Math.max(px, rx) + 1e-10;
    const minY: number = Math.min(py, ry) - 1e-10;
    const maxY: number = Math.max(py, ry) + 1e-10;

    return qx >= minX && qx <= maxX && qy >= minY && qy <= maxY;
  }

  function isPointInPolygonLL(point: Position, polygon: GeoPolygon[]): boolean {
    if (polygon.length < 3) {
      return false;
    }

    const x: number = point.lng;
    const y: number = point.lat;
    let inside: boolean = false;
    let onBoundary: boolean = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const pi: GeoPolygon = polygon[i];
      const pj: GeoPolygon = polygon[j];

      if (includeBoundary && onSegmentLL(pj, { lat: y, lng: x }, pi)) {
        onBoundary = true;
        break;
      }

      const xi: number = pi.lng;
      const yi: number = pi.lat;
      const xj: number = pj.lng;
      const yj: number = pj.lat;

      if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }

    return onBoundary || inside;
  }

  const bboxes: { minX: number; minY: number; maxX: number; maxY: number }[] = polygons.map(
    (poly: GeoPolygon[]) => {
      if (poly.length === 0) {
        return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
      }

      let minX: number = poly[0].lng;
      let minY: number = poly[0].lat;
      let maxX: number = poly[0].lng;
      let maxY: number = poly[0].lat;

      for (const p of poly) {
        if (p.lng < minX) minX = p.lng;
        if (p.lng > maxX) maxX = p.lng;
        if (p.lat < minY) minY = p.lat;
        if (p.lat > maxY) maxY = p.lat;
      }

      return { minX, minY, maxX, maxY };
    },
  );

  for (let j = 0; j < polygons.length; j++) {
    const polygon = polygons[j];
    if (polygon.length < 3) {
      continue;
    }

    const bbox = bboxes[j];

    if (
      point.lng < bbox.minX ||
      point.lng > bbox.maxX ||
      point.lat < bbox.minY ||
      point.lat > bbox.maxY
    ) {
      continue;
    }

    if (isPointInPolygonLL(point, polygon)) {
      return true;
    }
  }

  return false;
}
export const findCenterOfPolygon = (multi: GeoPolygon[]): { lat: number; lng: number } => {
  const defaultC = { lat: 0, lng: 0 };

  const points =
    multi?.filter(
      (p: any): p is Position =>
        p?.lat !== undefined &&
        p?.lng !== undefined &&
        Number.isFinite(p.lat) &&
        Number.isFinite(p.lng),
    ) ?? [];
  if (points.length === 0) return defaultC;

  const { minLat, maxLat, minLng, maxLng } = points.reduce(
    (acc, { lat, lng }) => ({
      minLat: Math.min(acc.minLat, lat),
      maxLat: Math.max(acc.maxLat, lat),
      minLng: Math.min(acc.minLng, lng),
      maxLng: Math.max(acc.maxLng, lng),
    }),
    { minLat: Infinity, maxLat: -Infinity, minLng: Infinity, maxLng: -Infinity },
  );

  return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
};

export const getCityFromGooglePlace = (placeData?: any) => {
  const components = placeData?.address_components || [];
  let postalTown = '';
  let localityPolitical = '';
  let adminAreaLevel1 = '';
  let establishmentOrNaturalFeature = '';

  for (const component of components) {
    const types = component.types || [];
    const name = component.long_name;

    if (!postalTown && types.includes('postal_town')) {
      postalTown = name;
    }

    if (!localityPolitical && types.includes('locality') && types.includes('political')) {
      localityPolitical = name;
    }

    if (!adminAreaLevel1 && types.includes('administrative_area_level_1')) {
      adminAreaLevel1 = name;
    }

    if (
      !establishmentOrNaturalFeature &&
      types.includes('establishment') &&
      types.includes('natural_feature')
    ) {
      establishmentOrNaturalFeature = name;
    }
  }

  const city = postalTown || localityPolitical || establishmentOrNaturalFeature;

  if (city && adminAreaLevel1 && city !== adminAreaLevel1) {
    return `${city}, ${adminAreaLevel1}`;
  }

  return city || adminAreaLevel1 || '';
};

export const getCityFromHotelSearchGA = (index?: number) => {
  const gaString = sessionStorage.getItem(getKey('hotelSearchGA', index));
  if (!gaString) {
    return '';
  }

  try {
    const parsed = JSON.parse(gaString);
    const place = parsed?.address_components ? parsed : parsed?.[0];
    return getCityFromGooglePlace(place);
  } catch {
    return '';
  }
};

export const getNationality = (user?: User) => {
  let nationality = 'IL';
  if (user?.department?.nationalityCode) {
    nationality = user.department.nationalityCode;
  }
  const isBranchNatonality = user?.branch?.ext_1_pcc?.toLowerCase().includes('nationality');
  if (isBranchNatonality) {
    const branchCodes = user?.branch?.ext_1_pcc.split(',');
    const nation = branchCodes?.find((code: string) => code.toLowerCase().includes('nationality'));
    if (nation) {
      nationality = nation.replace(/nationality:/i, '')?.toUpperCase();
    }
  }
  return nationality;
};

export interface HotelDisplaySettings {
  priceMode: string;
  currency: string;
}

export const getHotelDisplaySettings = (hotelIndex?: number): HotelDisplaySettings => {
  const searchToken = sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex));
  const currency = sessionStorage.getItem(getKey('displayCurrencyHotels', hotelIndex)) || 'usd';

  if (searchToken) {
    const stored = sessionStorage.getItem(getKey('hotelDisplaySettingsByToken', hotelIndex));
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {}
    }
  }

  return { priceMode: '0', currency };
};

export const saveHotelDisplaySettings = (settings: HotelDisplaySettings, hotelIndex?: number) => {
  const searchToken = sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex));

  if (searchToken) {
    sessionStorage.setItem(
      getKey('hotelDisplaySettingsByToken', hotelIndex),
      JSON.stringify(settings),
    );
  }

  sessionStorage.setItem(getKey('displayCurrencyHotels', hotelIndex), settings.currency);
};

const proposalAvailableLanguages = [
  { lang: 'en' },
  { lang: 'he' },
  { lang: 'it' },
  { lang: 'es' },
  { lang: 'fr' },
];

export const getProposalLanguageFallback = () => {
  const initSiteData = JSON.parse(sessionStorage.getItem('initSiteData') || '{}');
  const nationality = initSiteData.other?.defaultNationality?.toLowerCase();
  return nationality === 'it' ? 'it' : 'he';
};

export const resolveProposalLanguageFromBranch = (
  ext1Pcc: string | undefined,
  fallback?: string,
) => {
  const resolvedFallback = fallback ?? getProposalLanguageFallback();
  if (!ext1Pcc) {
    return resolvedFallback;
  }
  for (const part of ext1Pcc.split(',')) {
    const colonIndex = part.indexOf(':');
    if (colonIndex === -1 || part.slice(0, colonIndex).trim().toUpperCase() !== 'PROPOSAL_LANG') {
      continue;
    }
    const proposalLang = part
      .slice(colonIndex + 1)
      .trim()
      .toLowerCase();
    return proposalAvailableLanguages.some((lang) => lang.lang === proposalLang)
      ? proposalLang
      : resolvedFallback;
  }
  return resolvedFallback;
};

export const isAiAssistantEnabledFromBranch = (ext1Pcc?: string) => {
  if (!ext1Pcc) {
    return false;
  }
  for (const part of ext1Pcc.split(',')) {
    const colonIndex = part.indexOf(':');
    if (colonIndex === -1) {
      continue;
    }
    const key = part.slice(0, colonIndex).trim().toUpperCase();
    const value = part.slice(colonIndex + 1).trim().toUpperCase();
    if (key === 'AI_ASSISTANT' && value === 'ON') {
      return true;
    }
  }
  return false;
};

export const getHotelNameFromHotelSearchGA = (hotelIndex?: number): string => {
  const gaString = sessionStorage.getItem(getKey('hotelSearchGA', hotelIndex));
  if (!gaString) {
    return '';
  }

  try {
    const parsed = JSON.parse(gaString);
    const place = parsed?.result ?? (parsed?.address_components ? parsed : parsed?.[0]);
    if (!place) {
      return '';
    }

    const types = place.types;
    const isHotel =
      Array.isArray(types) && (types.includes('establishment') || types.includes('lodging'));

    if (!isHotel) {
      return '';
    }

    return typeof place.name === 'string' ? place.name.trim() : '';
  } catch {
    return '';
  }
};

export const parseRoomsOccupancy = (roomsString: string) => {
  const rooms = (roomsString || '')
    .split(';')
    .map((room) => room.trim())
    .filter(Boolean);

  let adults = 0;
  const childrenAges: number[] = [];

  rooms.forEach((room) => {
    const [adultsRaw, ...agesRaw] = room.split('_');
    const roomAdults = Number.parseInt(adultsRaw, 10);
    if (Number.isFinite(roomAdults) && roomAdults > 0) {
      adults += roomAdults;
    }
    agesRaw.forEach((ageRaw) => {
      const age = Number.parseInt(ageRaw, 10);
      if (Number.isFinite(age) && age >= 0) {
        childrenAges.push(age);
      }
    });
  });

  return {
    adults,
    children: childrenAges.length,
    childrenAges,
  };
};

export const resolveChildrenOccupancy = (children?: number, childrenAges?: number[]) => {
  const ages = (childrenAges || []).filter((age) => Number.isFinite(age) && age >= 0);
  if (ages.length > 0) {
    return { children: ages.length, childrenAges: ages };
  }

  return { children: children || 0 };
};

export const resolveHotelInfo = (information?: any): any | undefined => {
  if (!information) {
    return undefined;
  }

  if ('hotelInfo' in information && information.hotelInfo) {
    return information.hotelInfo;
  }

  if ('hotelDetails' in information && information.hotelDetails) {
    return information.hotelDetails;
  }

  if ('checkIn' in information || 'images' in information || 'hotelFacilities' in information) {
    return information;
  }

  return undefined;
};

export const createDefaultHotelInfoConfig = (hotelInfo?: any, i18nLang = 'en'): any => ({
  language: getDefaultHotelInfoLanguage(hotelInfo, i18nLang),
  imagesIndexes: [],
});

export const getDefaultHotelInfoLanguage = (hotelInfo?: any, i18nLang = 'en') => {
  const languages = hotelInfo?.hotelInfoLangs?.map((lang: any) => lang.languageCode) || [];

  if (!languages.length) {
    return i18nLang;
  }

  if (languages.includes(i18nLang)) {
    return i18nLang;
  }

  if (i18nLang === 'he' && languages.includes('he')) {
    return 'he';
  }

  return languages[0];
};
